import axios from 'axios';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
const ACCESS_TOKEN_KEY = 'vihs_access_token';

const api = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

let refreshPromise = null;

function getAccessToken() {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

function setAccessToken(token) {
  if (token) localStorage.setItem(ACCESS_TOKEN_KEY, token);
  else localStorage.removeItem(ACCESS_TOKEN_KEY);
}

function clearSession() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem('token'); // legado
}

api.interceptors.request.use((config) => {
  const token = getAccessToken() || localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    if (typeof config.headers?.delete === 'function') {
      config.headers.delete('Content-Type');
    } else if (config.headers) {
      delete config.headers['Content-Type'];
    }
  }
  return config;
});

async function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = api
      .post('/auth/refresh')
      .then((response) => {
        const token = response.data?.token;
        if (token) setAccessToken(token);
        return token;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    if (
      status === 401 &&
      original &&
      !original._retry &&
      !String(original.url || '').includes('/auth/login') &&
      !String(original.url || '').includes('/auth/register') &&
      !String(original.url || '').includes('/auth/refresh')
    ) {
      original._retry = true;
      try {
        const token = await refreshAccessToken();
        if (token) {
          original.headers.Authorization = `Bearer ${token}`;
          return api(original);
        }
      } catch {
        clearSession();
      }
    }

    return Promise.reject(error);
  }
);

const getErrorMessage = (error, fallback) =>
  error.response?.data?.message || error.message || fallback;

function createApiError(error, fallback) {
  const err = new Error(getErrorMessage(error, fallback));
  err.code = error.response?.data?.code || null;
  err.status = error.response?.status || null;
  err.meta = error.response?.data?.meta || null;
  return err;
}

function normalizeAuthResponse(data) {
  if (data?.token) setAccessToken(data.token);
  return data;
}

export const authService = {
  getDiscordAuthUrl() {
    return `${API_BASE_URL}/api/v1/auth/discord`;
  },

  getDiscordLinkUrl() {
    return `${API_BASE_URL}/api/v1/auth/discord/link-url`;
  },

  async getDiscordLinkAuthorizeUrl() {
    try {
      const response = await api.get('/auth/discord/link-url');
      return response.data?.data?.url || this.getDiscordAuthUrl();
    } catch (error) {
      throw createApiError(error, 'Erro ao iniciar vínculo com Discord');
    }
  },

  async login(credentials) {
    try {
      const response = await api.post('/auth/login', credentials);
      return normalizeAuthResponse(response.data);
    } catch (error) {
      throw createApiError(error, 'Erro ao fazer login');
    }
  },

  async register(userData) {
    try {
      const response = await api.post('/auth/register', userData);
      return normalizeAuthResponse(response.data);
    } catch (error) {
      throw createApiError(error, 'Erro ao criar conta');
    }
  },

  async refresh() {
    try {
      const response = await api.post('/auth/refresh');
      return normalizeAuthResponse(response.data);
    } catch (error) {
      clearSession();
      throw new Error(getErrorMessage(error, 'Sessão expirada'));
    }
  },

  async forgotPassword(email) {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao solicitar recuperação de senha'));
    }
  },

  async resetPassword(token, payload) {
    try {
      const response = await api.patch(`/auth/reset-password/${token}`, payload);
      return normalizeAuthResponse(response.data);
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao redefinir senha'));
    }
  },

  async verifyEmail(token) {
    try {
      const response = await api.get(`/auth/verify-email/${token}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao verificar e-mail'));
    }
  },

  async verifyEmailCode(code) {
    try {
      const response = await api.post('/auth/verify-email-code', { code });
      return response.data;
    } catch (error) {
      const err = new Error(getErrorMessage(error, 'Código inválido'));
      err.code = error.response?.data?.code || error.response?.data?.errorCode;
      throw err;
    }
  },

  async resendVerification() {
    try {
      const response = await api.post('/auth/resend-verification');
      return response.data;
    } catch (error) {
      const err = new Error(getErrorMessage(error, 'Não foi possível reenviar o código'));
      err.code = error.response?.data?.code || error.response?.data?.errorCode;
      throw err;
    }
  },

  async getCurrentUser() {
    try {
      const response = await api.get('/auth/me');
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) clearSession();
      throw new Error(getErrorMessage(error, 'Erro ao obter informações do usuário'));
    }
  },

  async updatePassword(payload) {
    try {
      const response = await api.patch('/auth/update-password', payload);
      return normalizeAuthResponse(response.data);
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao atualizar senha'));
    }
  },

  async listSessions() {
    try {
      const response = await api.get('/auth/sessions');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao listar sessões'));
    }
  },

  async revokeSession(sessionId) {
    try {
      const response = await api.delete(`/auth/sessions/${sessionId}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao encerrar sessão'));
    }
  },

  async logout() {
    try {
      await api.post('/auth/logout');
    } catch {
      // limpa mesmo se falhar
    } finally {
      clearSession();
    }
  },

  async logoutAll() {
    try {
      await api.post('/auth/logout-all');
    } catch {
      // limpa mesmo se falhar
    } finally {
      clearSession();
    }
  },

  setAccessToken,
  getAccessToken,
  clearSession,
};

export const userService = {
  async updateMe(payload) {
    try {
      const response = await api.patch('/users/me', payload);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao atualizar perfil'));
    }
  },

  async updateAvatar(file) {
    try {
      const formData = new FormData();
      formData.append('avatar', file, file.name || 'avatar.jpg');
      const response = await api.post('/users/me/avatar', formData);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao atualizar a foto de perfil'));
    }
  },

  async deleteAvatar() {
    try {
      const response = await api.delete('/users/me/avatar');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao remover a foto de perfil'));
    }
  },

  async updatePreferredLanguage(language) {
    try {
      const response = await api.patch('/users/me/language', { language });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao atualizar idioma'));
    }
  },

  async getPublicProfile(username) {
    try {
      const response = await api.get(`/users/profile/${encodeURIComponent(username)}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao buscar perfil'));
    }
  },

  async getSuggestedProfiles(limit = 5) {
    try {
      const response = await api.get('/users/suggested', { params: { limit } });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao carregar usuários sugeridos'));
    }
  },

  async searchUsers(q, limit = 12) {
    try {
      const response = await api.get('/users/search', { params: { q, limit } });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao buscar usuários'));
    }
  },

  async deactivateMe() {
    try {
      const response = await api.delete('/users/me');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao desativar conta'));
    }
  },

  async getAdminOverview(params = {}) {
    try {
      const response = await api.get('/users/admin/overview', { params });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao carregar o painel admin'));
    }
  },

  async updateUserRole(userId, role) {
    try {
      const response = await api.patch(`/users/admin/${userId}/role`, { role });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao atualizar cargo'));
    }
  },

  async updateUserStatus(userId, status) {
    try {
      const response = await api.patch(`/users/admin/${userId}/status`, { status });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao atualizar status'));
    }
  },

  async deleteUser(userId) {
    try {
      const response = await api.delete(`/users/admin/${userId}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao excluir conta'));
    }
  },

  async forcePasswordReset(userId) {
    try {
      const response = await api.post(`/users/admin/${userId}/force-password-reset`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao forçar reset de senha'));
    }
  },

  async cancelPasswordReset(userId) {
    try {
      const response = await api.post(`/users/admin/${userId}/cancel-password-reset`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao cancelar reset de senha'));
    }
  },

  async revokeUserSessions(userId) {
    try {
      const response = await api.post(`/users/admin/${userId}/revoke-sessions`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao encerrar sessões'));
    }
  },

  async verifyUserEmail(userId) {
    try {
      const response = await api.post(`/users/admin/${userId}/verify-email`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao verificar e-mail'));
    }
  },

  async listBadges() {
    try {
      const response = await api.get('/users/admin/badges');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao carregar cargos'));
    }
  },

  async createBadge(payload) {
    try {
      const response = await api.post('/users/admin/badges', payload);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao criar cargo'));
    }
  },

  async updateBadge(badgeId, payload) {
    try {
      const response = await api.patch(`/users/admin/badges/${badgeId}`, payload);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao atualizar cargo'));
    }
  },

  async deleteBadge(badgeId) {
    try {
      const response = await api.delete(`/users/admin/badges/${badgeId}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao excluir cargo'));
    }
  },

  async updateUserBadges(userId, badgeIds) {
    try {
      const response = await api.put(`/users/admin/${userId}/badges`, { badgeIds });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao atualizar cargos'));
    }
  },

  async updateUserVerification(userId, verified) {
    try {
      const response = await api.patch(`/users/admin/${userId}/verification`, { verified });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao atualizar selo de verificação'));
    }
  },

  async updateSystemAccount(userId, payload) {
    try {
      const response = await api.patch(`/users/admin/${userId}/system-account`, payload);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao atualizar conta institucional'));
    }
  },
};

export const socialService = {
  async follow(username) {
    try {
      const response = await api.post(`/social/follow/${encodeURIComponent(username)}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao seguir usuário'));
    }
  },

  async unfollow(username) {
    try {
      const response = await api.delete(`/social/follow/${encodeURIComponent(username)}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao deixar de seguir'));
    }
  },

  async getFollowers(username, params = {}) {
    try {
      const response = await api.get(`/social/${encodeURIComponent(username)}/followers`, { params });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao carregar seguidores'));
    }
  },

  async getFollowing(username, params = {}) {
    try {
      const response = await api.get(`/social/${encodeURIComponent(username)}/following`, { params });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao carregar quem segue'));
    }
  },

  async getFeed(params = {}) {
    try {
      const response = await api.get('/social/feed', { params });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao carregar o feed'));
    }
  },

  async getUserPosts(username, params = {}) {
    try {
      const response = await api.get(`/social/${encodeURIComponent(username)}/posts`, { params });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao carregar publicações'));
    }
  },

  async getUserHub(username) {
    try {
      const response = await api.get(`/social/${encodeURIComponent(username)}/hub`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao carregar o Dev Hub'));
    }
  },

  async createHubCategory(payload) {
    try {
      const response = await api.post('/social/hub/categories', payload);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao criar categoria'));
    }
  },

  async updateHubCategory(categoryId, payload) {
    try {
      const response = await api.patch(`/social/hub/categories/${categoryId}`, payload);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao atualizar categoria'));
    }
  },

  async deleteHubCategory(categoryId) {
    try {
      const response = await api.delete(`/social/hub/categories/${categoryId}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao excluir categoria'));
    }
  },

  async createHubItem(categoryId, payload) {
    try {
      const formData = new FormData();
      Object.entries(payload || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && key !== 'media') {
          formData.append(key, String(value));
        }
      });
      if (typeof File !== 'undefined' && payload?.media instanceof File) {
        formData.append('media', payload.media);
      }
      const response = await api.post(`/social/hub/categories/${categoryId}/items`, formData);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao criar sub-postagem'));
    }
  },

  async updateHubItem(itemId, payload) {
    try {
      const formData = new FormData();
      Object.entries(payload || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && key !== 'media') {
          formData.append(key, String(value));
        }
      });
      if (typeof File !== 'undefined' && payload?.media instanceof File) {
        formData.append('media', payload.media);
      }
      const response = await api.patch(`/social/hub/items/${itemId}`, formData);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao atualizar sub-postagem'));
    }
  },

  async deleteHubItem(itemId) {
    try {
      const response = await api.delete(`/social/hub/items/${itemId}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao excluir sub-postagem'));
    }
  },

  async createPost(payload) {
    try {
      const hasMedia = typeof File !== 'undefined' && payload?.image instanceof File;
      if (hasMedia) {
        const formData = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          if (value !== undefined && value !== null && key !== 'image') {
            formData.append(key, String(value));
          }
        });
        formData.append('image', payload.image);
        const response = await api.post('/social/posts', formData);
        return response.data;
      }

      const response = await api.post('/social/posts', typeof payload === 'string' ? { content: payload } : payload);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao publicar'));
    }
  },

  async translatePost(postId, targetLanguage) {
    try {
      const response = await api.post(`/social/posts/${postId}/translate`, { targetLanguage });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao traduzir publicação'));
    }
  },

  async translateHubItem(itemId, targetLanguage) {
    try {
      const response = await api.post(`/social/hub/items/${itemId}/translate`, { targetLanguage });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao traduzir sub-postagem'));
    }
  },

  async deletePost(postId) {
    try {
      const response = await api.delete(`/social/posts/${postId}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao excluir publicação'));
    }
  },

  async toggleLike(postId) {
    try {
      const response = await api.post(`/social/posts/${postId}/like`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao curtir publicação'));
    }
  },

  async createReport(payload) {
    try {
      const response = await api.post('/social/reports', payload);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao enviar denúncia'));
    }
  },

  async getAdminReports(params = {}) {
    try {
      const response = await api.get('/social/admin/reports', { params });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao carregar denúncias'));
    }
  },

  async reviewReport(reportId, status) {
    try {
      const response = await api.patch(`/social/admin/reports/${reportId}`, { status });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao revisar denúncia'));
    }
  },

  async getNotifications(params = {}) {
    try {
      const response = await api.get('/social/notifications', { params });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao carregar notificações'));
    }
  },

  async getUnreadNotificationCount() {
    try {
      const response = await api.get('/social/notifications/unread-count');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao carregar contagem de notificações'));
    }
  },

  async markNotificationRead(notificationId) {
    try {
      const response = await api.post(`/social/notifications/${notificationId}/read`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao marcar notificação'));
    }
  },

  async markAllNotificationsRead() {
    try {
      const response = await api.post('/social/notifications/read-all');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao marcar notificações'));
    }
  },
};

export const settingsService = {
  async getPublic() {
    try {
      const response = await api.get('/settings/public');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao carregar as configurações do site'));
    }
  },

  async update(payload) {
    try {
      const response = await api.patch('/settings/admin', payload);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao salvar as configurações do site'));
    }
  },
};

export const studioService = {
  async listTeam() {
    try {
      const response = await api.get('/studio/team');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao carregar a equipe'));
    }
  },

  async listTeamAdmin() {
    try {
      const response = await api.get('/studio/team/admin');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao carregar a equipe do Studio'));
    }
  },

  async createMember(payload) {
    try {
      const response = await api.post('/studio/team', payload);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao adicionar membro'));
    }
  },

  async updateMember(id, payload) {
    try {
      const response = await api.patch(`/studio/team/${id}`, payload);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao atualizar membro'));
    }
  },

  async deleteMember(id) {
    try {
      const response = await api.delete(`/studio/team/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao remover membro'));
    }
  },
};

export const contactService = {
  async submitProjectRequest(payload) {
    try {
      const response = await api.post('/contact/project', payload);
      return response.data;
    } catch (error) {
      throw createApiError(error, 'Erro ao enviar a solicitação de projeto');
    }
  },

  async listMyRequests() {
    try {
      const response = await api.get('/contact/my-requests');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao carregar seus pedidos'));
    }
  },

  async getMyRequest(id) {
    try {
      const response = await api.get(`/contact/my-requests/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao carregar o pedido'));
    }
  },

  async listAdminRequests(params = {}) {
    try {
      const response = await api.get('/contact/admin/requests', { params });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao carregar pedidos'));
    }
  },

  async getAdminRequest(id) {
    try {
      const response = await api.get(`/contact/admin/requests/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao carregar o pedido'));
    }
  },

  async updateAdminRequest(id, payload) {
    try {
      const response = await api.patch(`/contact/admin/requests/${id}`, payload);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao atualizar o pedido'));
    }
  },
};

export const financeService = {
  async bootstrap() {
    try {
      const response = await api.post('/finance/bootstrap');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao preparar o ambiente financeiro'));
    }
  },

  async getOverview(params = {}) {
    try {
      const response = await api.get('/finance/overview', { params });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao carregar o panorama financeiro'));
    }
  },

  async listAccounts(params = {}) {
    try {
      const response = await api.get('/finance/accounts', { params });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao carregar contas'));
    }
  },

  async createAccount(payload) {
    try {
      const response = await api.post('/finance/accounts', payload);
      return response.data;
    } catch (error) {
      throw createApiError(error, 'Erro ao criar conta');
    }
  },

  async updateAccount(id, payload) {
    try {
      const response = await api.patch(`/finance/accounts/${id}`, payload);
      return response.data;
    } catch (error) {
      throw createApiError(error, 'Erro ao atualizar conta');
    }
  },

  async deleteAccount(id) {
    try {
      const response = await api.delete(`/finance/accounts/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao remover conta'));
    }
  },

  async listCategories(params = {}) {
    try {
      const response = await api.get('/finance/categories', { params });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao carregar categorias'));
    }
  },

  async createCategory(payload) {
    try {
      const response = await api.post('/finance/categories', payload);
      return response.data;
    } catch (error) {
      throw createApiError(error, 'Erro ao criar categoria');
    }
  },

  async updateCategory(id, payload) {
    try {
      const response = await api.patch(`/finance/categories/${id}`, payload);
      return response.data;
    } catch (error) {
      throw createApiError(error, 'Erro ao atualizar categoria');
    }
  },

  async deleteCategory(id) {
    try {
      const response = await api.delete(`/finance/categories/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao remover categoria'));
    }
  },

  async listTransactions(params = {}) {
    try {
      const response = await api.get('/finance/transactions', { params });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao carregar lançamentos'));
    }
  },

  async createTransaction(payload) {
    try {
      const response = await api.post('/finance/transactions', payload);
      return response.data;
    } catch (error) {
      throw createApiError(error, 'Erro ao registrar lançamento');
    }
  },

  async updateTransaction(id, payload) {
    try {
      const response = await api.patch(`/finance/transactions/${id}`, payload);
      return response.data;
    } catch (error) {
      throw createApiError(error, 'Erro ao atualizar lançamento');
    }
  },

  async deleteTransaction(id) {
    try {
      const response = await api.delete(`/finance/transactions/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao remover lançamento'));
    }
  },

  async listBudgets(params = {}) {
    try {
      const response = await api.get('/finance/budgets', { params });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao carregar orçamentos'));
    }
  },

  async upsertBudget(payload) {
    try {
      const response = await api.put('/finance/budgets', payload);
      return response.data;
    } catch (error) {
      throw createApiError(error, 'Erro ao salvar orçamento');
    }
  },

  async deleteBudget(id) {
    try {
      const response = await api.delete(`/finance/budgets/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao remover orçamento'));
    }
  },

  async listGoals() {
    try {
      const response = await api.get('/finance/goals');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao carregar metas'));
    }
  },

  async createGoal(payload) {
    try {
      const response = await api.post('/finance/goals', payload);
      return response.data;
    } catch (error) {
      throw createApiError(error, 'Erro ao criar meta');
    }
  },

  async updateGoal(id, payload) {
    try {
      const response = await api.patch(`/finance/goals/${id}`, payload);
      return response.data;
    } catch (error) {
      throw createApiError(error, 'Erro ao atualizar meta');
    }
  },

  async deleteGoal(id) {
    try {
      const response = await api.delete(`/finance/goals/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Erro ao remover meta'));
    }
  },
};

export default api;
